import Component from '@glimmer/component';
import type { ComponentLike } from '@glint/template';
import { PrismBadge, PrismModal } from '@mylighthouse/prism-ember';
import { t, tKey } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralRoiTrophiesContentAutopilotAnniversary from 'lumen/components/general/roi-trophies/content/autopilot-anniversary';
import GeneralRoiTrophiesContentMarketLeader from 'lumen/components/general/roi-trophies/content/market-leader';
import GeneralRoiTrophiesContentPricePushMilestone from 'lumen/components/general/roi-trophies/content/price-push-milestone';
import GeneralRoiTrophiesContentProductLifetime from 'lumen/components/general/roi-trophies/content/product-lifetime';
import GeneralRoiTrophiesContentReservationGeneral from 'lumen/components/general/roi-trophies/content/reservation-general';
import GeneralRoiTrophiesContentReservationMilestone from 'lumen/components/general/roi-trophies/content/reservation-milestone';
import GeneralRoiTrophiesContentReservationPricePush from 'lumen/components/general/roi-trophies/content/reservation-price-push';
import GeneralRoiTrophiesContentRevenueGeneral from 'lumen/components/general/roi-trophies/content/revenue-general';
import GeneralRoiTrophiesContentRevenuePricePush from 'lumen/components/general/roi-trophies/content/revenue-price-push';
import type Highlight from 'lumen/models/roi-trophies/highlight';
import type { HighlightType } from 'lumen/models/roi-trophies/highlight';
import { HIGHLIGHT_TYPE } from 'lumen/models/roi-trophies/highlight';
import { TROPHY_TYPES } from 'lumen/utils/roi-trophy/constants';
import type { ActiveTrophy } from 'lumen/utils/roi-trophy/types';

type WeeklyHighlightContent = ComponentLike<{ Args: { highlight: Highlight } }>;

const WEEKLY_HIGHLIGHT_COMPONENTS: Record<
  HighlightType,
  WeeklyHighlightContent
> = {
  [HIGHLIGHT_TYPE.AUTOPILOT_ANNIVERSARY]:
    GeneralRoiTrophiesContentAutopilotAnniversary,
  [HIGHLIGHT_TYPE.MARKET_LEADER]: GeneralRoiTrophiesContentMarketLeader,
  [HIGHLIGHT_TYPE.PRODUCT_LIFETIME]: GeneralRoiTrophiesContentProductLifetime,
  [HIGHLIGHT_TYPE.RESERVATION_GENERAL]:
    GeneralRoiTrophiesContentReservationGeneral,
  [HIGHLIGHT_TYPE.RESERVATION_MILESTONE]:
    GeneralRoiTrophiesContentReservationMilestone,
  [HIGHLIGHT_TYPE.RESERVATION_PRICE_PUSH]:
    GeneralRoiTrophiesContentReservationPricePush,
  [HIGHLIGHT_TYPE.REVENUE_GENERAL]: GeneralRoiTrophiesContentRevenueGeneral,
  [HIGHLIGHT_TYPE.REVENUE_PRICE_PUSH]:
    GeneralRoiTrophiesContentRevenuePricePush,
};

const MILESTONE_BADGE_KEY = tKey('roi_trophies.badge.milestone');
const NEW_RECORD_BADGE_KEY = tKey('roi_trophies.badge.new_record');

const WEEKLY_HIGHLIGHT_BADGE_KEYS: Record<HighlightType, string> = {
  [HIGHLIGHT_TYPE.AUTOPILOT_ANNIVERSARY]: MILESTONE_BADGE_KEY,
  [HIGHLIGHT_TYPE.MARKET_LEADER]: MILESTONE_BADGE_KEY,
  [HIGHLIGHT_TYPE.PRODUCT_LIFETIME]: MILESTONE_BADGE_KEY,
  [HIGHLIGHT_TYPE.RESERVATION_MILESTONE]: MILESTONE_BADGE_KEY,
  [HIGHLIGHT_TYPE.RESERVATION_PRICE_PUSH]: NEW_RECORD_BADGE_KEY,
  [HIGHLIGHT_TYPE.REVENUE_PRICE_PUSH]: NEW_RECORD_BADGE_KEY,
  [HIGHLIGHT_TYPE.REVENUE_GENERAL]: NEW_RECORD_BADGE_KEY,
  [HIGHLIGHT_TYPE.RESERVATION_GENERAL]: NEW_RECORD_BADGE_KEY,
};

interface GeneralRoiTrophiesTrophyModalSignature {
  Args: {
    activeTrophy: ActiveTrophy | null;
    onDismiss: () => void;
  };
}

export default class GeneralRoiTrophiesTrophyModal extends Component<GeneralRoiTrophiesTrophyModalSignature> {
  get badgeKey(): string {
    const { activeTrophy } = this.args;
    if (!activeTrophy) {
      return '';
    }

    if (activeTrophy.type === TROPHY_TYPES.WEEKLY_HIGHLIGHT) {
      return WEEKLY_HIGHLIGHT_BADGE_KEYS[activeTrophy.highlight.highlightType];
    }

    return MILESTONE_BADGE_KEY;
  }

  get milestone(): number {
    const { activeTrophy } = this.args;

    return activeTrophy?.type === TROPHY_TYPES.PRICE_PUSH_MILESTONE
      ? activeTrophy.milestone
      : 0;
  }

  get weeklyHighlightContext(): {
    component: WeeklyHighlightContent;
    highlight: Highlight;
  } | null {
    const { activeTrophy } = this.args;

    if (activeTrophy?.type !== TROPHY_TYPES.WEEKLY_HIGHLIGHT) {
      return null;
    }

    const { highlight } = activeTrophy;
    const component = WEEKLY_HIGHLIGHT_COMPONENTS[highlight.highlightType];

    return component ? { component, highlight } : null;
  }

  <template>
    <PrismModal @onClose={{@onDismiss}}>
      <:headerContent>
        {{! pl-1000 offsets the modal's built-in close button to visually center the badge,
            avoiding the need to override the modal header's internal styles }}
        <div class="flex w-full items-center justify-center pl-1000">
          {{#if this.badgeKey}}
            <PrismBadge @badgeType="brand" @label={{t this.badgeKey}} />
          {{/if}}
        </div>
      </:headerContent>
      <:default>
        <div class="flex flex-col items-center">
          {{#if (eq @activeTrophy.type TROPHY_TYPES.PRICE_PUSH_MILESTONE)}}
            <GeneralRoiTrophiesContentPricePushMilestone
              @milestone={{this.milestone}}
            />
          {{else if this.weeklyHighlightContext}}
            <this.weeklyHighlightContext.component
              @highlight={{this.weeklyHighlightContext.highlight}}
            />
          {{/if}}
        </div>
      </:default>
    </PrismModal>
  </template>
}
